import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, Switch, Button, Alert, CircularProgress, MenuItem,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import ConditionBuilder, { buildConditionsJson, parseConditionsJson } from "./ConditionBuilder";
import ActionBuilder, { buildActionsArray, parseActionsArray } from "./ActionBuilder";
import SearchableSelect from "../shared/SearchableSelect";
import { useRuleConditionFields } from "../../hooks/useRuleConditionFields";

const EditRuleDialog = ({ open, rule, onClose, onSaved }) => {
    const [form, setForm] = useState({ name: "", description: "", active: true, transaction_types: [], segment_ids: [] });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [actionCatalog, setActionCatalog] = useState([]);
    const [conditionCatalog, setConditionCatalog] = useState([]);
    const [uiHints, setUiHints] = useState({});
    const [transactionTypeOptions, setTransactionTypeOptions] = useState([]);
    const [etLoading, setEtLoading] = useState(false);
    const [segmentOptions, setSegmentOptions] = useState([]);
    const [segmentsLoading, setSegmentsLoading] = useState(false);

    const [actionState, setActionState] = useState([]);
    const [combinator, setCombinator] = useState("and");
    const [condRows, setCondRows] = useState([]);

    const handleConditionsChange = (newCombinator, newRows) => {
        setCombinator(newCombinator);
        setCondRows(newRows);
    };

    const primaryTransactionType = form.transaction_types?.[0] || rule?.transaction_type || "";

    const {
        ruleHints,
        payloadFields,
        payloadPaths,
        hasPayloadSchema,
    } = useRuleConditionFields(rule?.brand, primaryTransactionType);

    useEffect(() => {
        if (!rule) return;

        setForm({
            name: rule.name || "",
            description: rule.description || "",
            active: rule.active ?? true,
            transaction_types: Array.isArray(rule.transaction_types) && rule.transaction_types.length > 0
                ? rule.transaction_types
                : (rule.transaction_type ? [rule.transaction_type] : []),
            segment_ids: Array.isArray(rule.segment_ids) ? rule.segment_ids.map((id) => String(id)) : [],
        });
        setError(null);

        // Parse existing conditions into builder state
        const parsed = parseConditionsJson(rule.conditions);
        setCombinator(parsed.combinator);
        setCondRows(parsed.rows);

        // Parse existing actions into ActionBuilder state
        setActionState(parseActionsArray(rule.actions, {
            ruleHints: null,
            transactionType: rule.transaction_type,
        }));

        // Fetch catalogs and payload schema
        const brand = rule.brand || "";
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/rule-conditions`, { headers: { "X-Brand": brand } })
            .then(res => res.ok ? res.json() : {})
            .then(data => {
                setConditionCatalog(data.conditions || []);
                setUiHints(data.uiHints || {});
            })
            .catch(err => console.error("Failed to load conditions catalog", err));

        loyaltyFetch(`${LOYALTY_API_BASE}/admin/rule-actions`, { headers: { "X-Brand": brand } })
            .then(res => res.ok ? res.json() : { actions: [] })
            .then(data => setActionCatalog(data.actions || []))
            .catch(err => console.error("Failed to load actions catalog", err));

        setEtLoading(true);
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types?active=true`, {
            headers: { "X-Brand": brand }
        })
            .then((r) => r.ok ? r.json() : [])
            .then((items) => setTransactionTypeOptions(Array.isArray(items) ? items : []))
            .catch(() => setTransactionTypeOptions([]))
            .finally(() => setEtLoading(false));

        setSegmentsLoading(true);
        const segQuery = `?brand=${encodeURIComponent(brand)}&active=true`;
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/ui-options/segments${segQuery}`, { headers: { "X-Brand": brand } })
            .then((r) => r.ok ? r.json() : null)
            .then((data) => setSegmentOptions(Array.isArray(data?.items) ? data.items : []))
            .catch(() => setSegmentOptions([]))
            .finally(() => setSegmentsLoading(false));
    }, [rule]);

    useEffect(() => {
        if (!rule?.actions || !ruleHints) return;
        setActionState(parseActionsArray(rule.actions, { ruleHints, transactionType: primaryTransactionType }));
    }, [rule?.actions, ruleHints, primaryTransactionType]);

    const handleSubmit = async () => {
        if (!rule) return;
        setSubmitting(true);
        setError(null);
        try {
            const conditions = buildConditionsJson(combinator, condRows);
            const actionsArr = buildActionsArray(actionState, { payloadFields });

            const body = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                transaction_type: form.transaction_types[0],
                transaction_types: form.transaction_types,
                segment_ids: Array.isArray(form.segment_ids) && form.segment_ids.length > 0 ? form.segment_ids : null,
                conditions,
                actions: actionsArr,
                active: form.active
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/rules/${rule.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "X-Brand": rule.brand },
                body: JSON.stringify(body),
            });
            if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
            onSaved(); onClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    if (!rule) return null;

    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rules.gradient }}>
                        <GavelIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier la Règle
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{rule.brand} • {rule.transaction_type}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>

                <Grid container spacing={2}>
                    {/* name & description */}
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom de la règle" value={form.name}
                            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} size="small"
                            sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} size="small" multiline rows={2}
                            sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12}>
                        <SearchableSelect
                            multiple
                            required
                            label={etLoading ? "Chargement des types…" : "Types de transaction"}
                            loading={etLoading}
                            disabled={etLoading}
                            options={transactionTypeOptions}
                            value={form.transaction_types}
                            onChange={(keys) => setForm((p) => ({ ...p, transaction_types: keys }))}
                            getOptionValue={(et) => et.key}
                            getOptionLabel={(et) => `${et.name} (${et.key})`}
                            helperText={`${transactionTypeOptions.length} type(s) — recherche disponible`}
                            sx={fieldSx}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <SearchableSelect
                            multiple
                            label={segmentsLoading ? "Chargement des segments…" : "Segments ciblés (optionnel)"}
                            loading={segmentsLoading}
                            disabled={segmentsLoading}
                            options={segmentOptions}
                            value={form.segment_ids}
                            onChange={(ids) => setForm((p) => ({ ...p, segment_ids: ids.map(String) }))}
                            getOptionValue={(s) => String(s.id)}
                            getOptionLabel={(s) => s.name || String(s.id)}
                            helperText={`${segmentOptions.length} segment(s) — recherche disponible`}
                            sx={fieldSx}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1", height: "100%" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Activer</Typography>
                                <Typography fontSize={11} color="text.secondary">
                                    Règle appliquée • Priorité auto: {rule.priority ?? 0}
                                </Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rules.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rules.main }
                                }} />
                        </Box>
                    </Grid>

                    {/* ── Condition Builder ── */}
                    <Grid item xs={12}>
                        <Typography fontSize={13} fontWeight={700} mb={1} color="#334155">
                            Conditions
                        </Typography>
                        <ConditionBuilder
                            brandName={rule.brand}
                            transactionType={primaryTransactionType}
                            conditionCatalog={conditionCatalog}
                            uiHints={uiHints}
                            combinator={combinator}
                            rows={condRows}
                            onChange={handleConditionsChange}
                            hasPayloadSchema={hasPayloadSchema}
                            payloadPaths={payloadPaths}
                        />
                    </Grid>

                    {/* ── Action Builder ── */}
                    <Grid item xs={12}>
                        <ActionBuilder
                            brandName={rule.brand}
                            actionCatalog={actionCatalog}
                            value={actionState}
                            onChange={setActionState}
                            ruleHints={ruleHints}
                            payloadFields={payloadFields}
                            transactionType={primaryTransactionType}
                        />
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.rules.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditRuleDialog;
